import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';
import styled from 'styled-components';

import { Avatar, Ellipsis, Tooltip, tokens } from '@treams/treams-ui';

import type { TreamsScoreRow } from '../TreamsScore.types';

const AVATAR_SIZE = 32;
const SHOWN_AVATAR_AMOUNT = 3;

type Manager = TreamsScoreRow['managers'][number];

// Avatar URLs live in the user directory, not the score snapshot, so they are
// resolved by manager ID and merged onto the Avatar's user prop.
const toAvatarUser = (manager: Manager, avatarByID?: Map<string, string>) => {
	const url = avatarByID?.get(manager.id);
	return {
		firstName: manager.firstName,
		lastName: manager.lastName,
		avatar: url ? { source: { url } } : undefined,
	};
};

// Trailing stack avatars get a white border so they read as separate circles
// while overlapping; the leftmost avatar stays borderless, identical to the
// single-manager rendering.
const StackedAvatar = styled(Avatar)`
	> div,
	img {
		border: 1px solid ${(props) => props.theme.colors.white};
	}
`;

const AvatarSlot = styled.div`
	display: flex;
	flex-shrink: 0;
	/* The cell's line-height otherwise leaves the first avatar on the text baseline. */
	line-height: 0;
`;

const OverlapItem = styled(motion.div)`
	/* Later siblings paint above earlier ones, like Avatar.Stack. */
	position: relative;
	display: flex;
	line-height: 0;
`;

const StackCounter = styled(Flex)`
	width: ${AVATAR_SIZE}px;
	height: ${AVATAR_SIZE}px;
	flex-shrink: 0;
	border-radius: 50%;
	background: #e0e0e0;
	color: #424242;
	font-weight: 500;
	font-size: 14px;
	align-items: center;
	justify-content: center;
`;

const fade = {
	initial: { opacity: 0, x: -5 },
	animate: { opacity: 1, x: 0 },
	exit: { opacity: 0, x: 5 },
	transition: { duration: 0.25 },
} as const;

// Avatars are 32px wide with a -16px overlap, so each extra stack item adds
// 16px of width. Animating the margin from -32px (no net width) to -16px makes
// the label slide along smoothly instead of jumping when avatars appear.
const stackFade = {
	initial: { opacity: 0, marginLeft: -32 },
	animate: { opacity: 1, marginLeft: -16 },
	exit: { opacity: 0, marginLeft: -32 },
	transition: { duration: 0.25 },
} as const;

const ManagerTooltip = ({ manager, children }: { manager: Manager; children: JSX.Element }) => (
	<Tooltip content={`${manager.firstName} ${manager.lastName}`} position="top" portal fitContent>
		{children}
	</Tooltip>
);

export const TreamsScoreManagers = ({
	managers,
	avatarByID,
}: {
	managers: Manager[];
	avatarByID?: Map<string, string>;
}) => {
	const { t } = useTranslation();

	const hasMultiple = managers.length > 1;
	const [first, ...rest] = managers;
	const shownRest = rest.slice(0, SHOWN_AVATAR_AMOUNT - 1);
	const overflowManagers = managers.slice(SHOWN_AVATAR_AMOUNT);
	const overflowCount = overflowManagers.length;

	const label = hasMultiple
		? t('analytics.treamsScore.table.managers', { count: managers.length })
		: `${first.firstName} ${first.lastName}`;

	return (
		<Flex alignItems="center" style={{ minWidth: 0, width: '100%' }}>
			<Flex alignItems="center" flexShrink={0}>
				{/* Leftmost avatar keeps the exact same slot in both states. */}
				<AvatarSlot>
					<ManagerTooltip manager={first}>
						<Avatar size={AVATAR_SIZE} user={toAvatarUser(first, avatarByID)} />
					</ManagerTooltip>
				</AvatarSlot>

				<AnimatePresence>
					{shownRest.map((manager) => (
						<OverlapItem key={manager.id} {...stackFade}>
							<ManagerTooltip manager={manager}>
								<StackedAvatar size={AVATAR_SIZE} user={toAvatarUser(manager, avatarByID)} />
							</ManagerTooltip>
						</OverlapItem>
					))}
					{overflowCount > 0 && (
						<OverlapItem key="__overflow" {...stackFade}>
							<Tooltip
								content={overflowManagers
									.map((manager) => `${manager.firstName} ${manager.lastName}`)
									.join('\n')}
								position="top"
								portal
								fitContent
							>
								<StackCounter>+{overflowCount}</StackCounter>
							</Tooltip>
						</OverlapItem>
					)}
				</AnimatePresence>
			</Flex>

			<AnimatePresence mode="popLayout">
				<motion.div key={label} {...fade} style={{ minWidth: 0, width: '100%', marginLeft: 8 }}>
					{hasMultiple ? (
						<span style={{ color: tokens.text.primary, whiteSpace: 'nowrap' }}>{label}</span>
					) : (
						<Ellipsis
							minWidth={0}
							width="100%"
							title={label}
							style={{ color: tokens.text.primary }}
						>
							{label}
						</Ellipsis>
					)}
				</motion.div>
			</AnimatePresence>
		</Flex>
	);
};
